import { getAccount, getUserSettings } from '@/api/auth';
import {
  applyCustomBetOdd,
  removeCustomSelection,
  toggleCustomSelection,
  undoLastCustomSelection,
  type CustomBetSelectionDraft,
  type CustomBetToggleResult,
} from '@/domain/custom-bets';
import {
  BETSLIP_STORAGE_KEY,
  DEFAULT_BETSLIP_STAKE,
  DEFAULT_MAX_COMBI,
  calculateTicket,
  clearTicket,
  countBets,
  createEmptyTicket,
  isSelectionInTicket,
  parseStoredTicket,
  removeSelection,
  setMode,
  setStake,
  setSystemMin,
  toggleBanked,
  toggleSelection,
  ticketTypeToMode,
  type BetslipMode,
  type BetslipSelectionDraft,
  type BetslipSelectionKeyParts,
  type BetslipSystemLimits,
  type BetslipTicket,
  type BetslipToggleOptions,
  type BetslipToggleResult,
} from '@/domain/betslip';
import {
  betslipLiveMaterialSignature,
  enrichBetslipTicketWithLiveFeed,
  type BetslipLiveFeedGetters,
} from '@/domain/betslip-live';
import {
  applyRewardToTicket,
  clearRewardFromTicket,
  type PlayerReward,
} from '@/domain/rewards';
import { resolveSystemLimitsFromSettings } from './build-draft';

type Listener = () => void;

let ticket: BetslipTicket = loadInitialTicket();
let version = 0;
const listeners = new Set<Listener>();

function loadInitialTicket(): BetslipTicket {
  if (typeof localStorage === 'undefined') return createEmptyTicket();
  try {
    const stored = parseStoredTicket(localStorage.getItem(BETSLIP_STORAGE_KEY));
    return stored ?? createEmptyTicket();
  } catch {
    return createEmptyTicket();
  }
}

function persist(next: BetslipTicket): void {
  ticket = next;
  version += 1;
  try {
    localStorage.setItem(BETSLIP_STORAGE_KEY, JSON.stringify(ticket));
  } catch {
    // Private browsing / quota — keep in-memory state.
  }
  for (const listener of listeners) listener();
}

function currentSystemLimits(): BetslipSystemLimits {
  return resolveSystemLimitsFromSettings(getUserSettings(), getAccount());
}

function readToggleOptions(options?: BetslipToggleOptions): BetslipToggleOptions {
  const systemLimits = currentSystemLimits();
  return {
    disableMultiCombo: options?.disableMultiCombo ?? false,
    maxCombi: options?.maxCombi ?? DEFAULT_MAX_COMBI,
    minSystem: options?.minSystem ?? systemLimits.minSystem,
    maxSystem: options?.maxSystem ?? systemLimits.maxSystem,
  };
}

export function subscribeBetslip(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getBetslipTicket(): BetslipTicket {
  return ticket;
}

export function getBetslipVersion(): number {
  return version;
}

/** Snapshot for `useSyncExternalStore` — the ticket object itself. */
export function getBetslipSnapshot(): BetslipTicket {
  return ticket;
}

export function getBetslipServerSnapshot(): BetslipTicket {
  return createEmptyTicket();
}

export function betslipToggle(
  draft: BetslipSelectionDraft,
  options?: BetslipToggleOptions,
): BetslipToggleResult {
  const result = toggleSelection(ticket, draft, readToggleOptions(options));
  if (result.ok) persist(result.ticket);
  return result;
}

export function betslipToggleCustom(
  draft: CustomBetSelectionDraft,
  options?: BetslipToggleOptions,
): CustomBetToggleResult {
  const result = toggleCustomSelection(ticket, draft, readToggleOptions(options));
  if (result.ok) persist(result.ticket);
  return result;
}

export function betslipRemoveCustomSelection(
  eventId: string | number,
  selectionKey: string,
): void {
  persist(removeCustomSelection(ticket, eventId, selectionKey, currentSystemLimits()));
}

/** Remove an entire Custom package for an event (`custom|{eventId}` row). */
export function betslipRemoveCustomPackage(eventId: string | number): void {
  const next = structuredClone(ticket);
  const index = next.Items.findIndex(
    (item) =>
      String(item.Event.EventId) === String(eventId) &&
      item.Bets.some((bet) => bet.Custom === true),
  );
  if (index < 0) return;
  next.Items.splice(index, 1);
  persist(calculateTicket(next, currentSystemLimits()));
}

export function betslipApplyCustomOdd(eventId: string | number, odd: number | null): void {
  persist(applyCustomBetOdd(ticket, eventId, odd, currentSystemLimits()));
}

export function betslipUndoLastCustomSelection(eventId: string | number): boolean {
  const result = undoLastCustomSelection(ticket, eventId, currentSystemLimits());
  if (!result.removed) return false;
  persist(result.ticket);
  return true;
}

export function betslipSetPossibleWinPending(pending: boolean): void {
  if (ticket.PossibleWinPending === pending) return;
  persist({ ...ticket, PossibleWinPending: pending });
}

export function betslipRemove(key: BetslipSelectionKeyParts): void {
  persist(removeSelection(ticket, key, currentSystemLimits()));
}

export function betslipClear(stake = DEFAULT_BETSLIP_STAKE): void {
  persist(clearTicket(stake));
}

/** Replace the slip with a full ticket (e.g. reuse-bet flow). */
export function betslipReplace(next: BetslipTicket): void {
  persist(calculateTicket(next, currentSystemLimits()));
}

export function betslipSetStake(stake: number): void {
  persist(setStake(ticket, stake, currentSystemLimits()));
}

export function betslipSetMode(mode: BetslipMode): void {
  persist(setMode(ticket, mode, currentSystemLimits()));
}

export function betslipSetSystemMin(systemMin: number): void {
  persist(setSystemMin(ticket, systemMin, currentSystemLimits()));
}

export function betslipToggleBanked(eventId: string | number): void {
  persist(toggleBanked(ticket, eventId, currentSystemLimits()));
}

/** Apply a FreeBet / BackupBet reward to the live slip. */
export function betslipApplyReward(reward: PlayerReward): void {
  persist(applyRewardToTicket(ticket, reward, currentSystemLimits()));
}

/** Clear the applied reward from the live slip. */
export function betslipClearReward(): void {
  if (ticket.RewardId == null && ticket.Rewards.length === 0) return;
  persist(clearRewardFromTicket(ticket, currentSystemLimits()));
}

/** Link an open ticket as KreditBet creditor (or clear when `null`). */
export function betslipSetCreditor(
  next:
    | {
        CreditorNumber: string;
        CreditorType: number;
        CreditorPossibleWin: number;
      }
    | null,
): void {
  if (next == null) {
    if (
      ticket.CreditorNumber === undefined &&
      ticket.CreditorType === undefined &&
      ticket.CreditorPossibleWin === undefined
    ) {
      return;
    }
    const cleared = { ...ticket };
    delete cleared.CreditorNumber;
    delete cleared.CreditorType;
    delete cleared.CreditorPossibleWin;
    persist(cleared);
    return;
  }
  persist({
    ...ticket,
    CreditorNumber: next.CreditorNumber,
    CreditorType: next.CreditorType,
    CreditorPossibleWin: next.CreditorPossibleWin,
  });
}

export function betslipSystemLimits(): BetslipSystemLimits {
  return currentSystemLimits();
}

export function betslipSelectionCount(): number {
  return countBets(ticket);
}

export function betslipHasSelection(parts: BetslipSelectionKeyParts): boolean {
  return isSelectionInTicket(ticket, parts);
}

export function betslipMode(): BetslipMode {
  return ticketTypeToMode(ticket.Type);
}

/**
 * Apply live feed enrichment to the current ticket.
 * Persists only when odds / blocked / scores / live / time actually change.
 */
export function betslipApplyLiveEnrichment(getters: BetslipLiveFeedGetters): boolean {
  const before = betslipLiveMaterialSignature(ticket);
  const next = enrichBetslipTicketWithLiveFeed(ticket, getters, currentSystemLimits());
  const after = betslipLiveMaterialSignature(next);
  if (before === after) return false;
  persist(next);
  return true;
}

/** Test helper — replace ticket without notifying (then call notify). */
export function __resetBetslipStoreForTests(next?: BetslipTicket): void {
  ticket = next ? calculateTicket(next, currentSystemLimits()) : createEmptyTicket();
  version += 1;
  try {
    if (next) localStorage.setItem(BETSLIP_STORAGE_KEY, JSON.stringify(ticket));
    else localStorage.removeItem(BETSLIP_STORAGE_KEY);
  } catch {
    // ignore
  }
  for (const listener of listeners) listener();
}
